interface OfflineData {
  rates: any[];
  timestamp: number;
  version: string;
}

class OfflineManager {
  private readonly STORAGE_KEY = 'nigeriafx_offline_data';
  private readonly VERSION = '1.0.0';
  private readonly MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

  async storeOfflineData(rates: any[]): Promise<void> {
    try {
      const data: OfflineData = {
        rates,
        timestamp: Date.now(),
        version: this.VERSION
      };
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      
      // Also store in IndexedDB for larger capacity
      if ('indexedDB' in window) {
        await this.storeInIndexedDB(data);
      }
    } catch (error) {
      console.error('Failed to store offline data:', error);
    }
  }

  getOfflineData(): any[] | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return null;

      const data: OfflineData = JSON.parse(stored);
      
      // Check if data is too old
      if (Date.now() - data.timestamp > this.MAX_AGE) {
        this.clearOfflineData();
        return null;
      }

      // Check version compatibility
      if (data.version !== this.VERSION) {
        this.clearOfflineData();
        return null;
      }

      return data.rates;
    } catch (error) {
      console.error('Failed to retrieve offline data:', error);
      return null;
    }
  }

  hasOfflineData(): boolean {
    return this.getOfflineData() !== null;
  }

  clearOfflineData(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.clearIndexedDB();
  }

  private async storeInIndexedDB(data: OfflineData): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('NigeriaFxDB', 1);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction(['rates'], 'readwrite');
        const store = transaction.objectStore('rates');
        store.put(data, 'latest');
        transaction.oncomplete = () => resolve();
      };
      
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains('rates')) {
          db.createObjectStore('rates');
        }
      };
    });
  }

  private clearIndexedDB(): void {
    const request = indexedDB.open('NigeriaFxDB', 1);
    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(['rates'], 'readwrite');
      const store = transaction.objectStore('rates');
      store.clear();
    };
  }
}

export const offlineManager = new OfflineManager();